/* Landing Page Styles */

/* Light mode */
.landing {
  --landing-pink: #ff83b8;
  --landing-pink-light: #ffb8d6;
  --landing-pink-dark: #e5609a;
  --landing-pink-bg: #fff0f5;
  --landing-pink-bg2: #fff8fb;
  --landing-bg: #ffffff;
  --landing-card-bg: #ffffff;
  --landing-card-border: #e5e7eb;
  --landing-text: #111827;
  --landing-text-secondary: #6b7280;
  --landing-text-muted: #9ca3af;
  --landing-border: #e5e7eb;
}

/* Links: no underline */
.landing a,
.landing a:hover {
  text-decoration: none;
}

/* Gradient text */
.gradientText {
  background: linear-gradient(135deg, #ff83b8, #ff5a9e);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/*
 * Entrance animations have been disabled for the landing page.
 *
 * The previous design started every `useFadeUp` element at
 * `opacity: 0; translateY(24px)` and only added `.fadeUpVisible`
 * once `useFadeUp`'s `IntersectionObserver` fired inside `useEffect`.
 * On SSR/hydrate that produced a very noticeable double flicker
 * for users on this route:
 *   1. SSR HTML lands with every section invisible (opacity:0).
 *   2. The root `.fadeIn` keyframe runs a 0.6s opacity 0→1 fade
 *      on the whole page.
 *   3. After `entry.client.tsx` boots and `useEffect` runs, each
 *      section's IntersectionObserver fires and animates
 *      opacity/translateY again over 0.5s.
 *
 * Keeping `.fadeIn` / `.fadeUp` as no-op identity classes
 * preserves the existing call sites (and the `useFadeUp` hook,
 * which still returns a usable ref) without forcing a
 * widespread refactor — but the visual contract is now
 * "elements are visible the moment they hit the DOM", so SSR
 * paint is the final paint and hydration is a silent no-op.
 *
 * If a per-element scroll-fade is wanted again later, do it via
 * a CSS-only animation (no JS-driven invisible→visible swap)
 * and keep the SSR rest state visible.
 */
.fadeIn,
.fadeUp,
.fadeUpVisible {
  /* No animation / opacity gating: SSR rest state is visible. */
}

/* Gallery scroll animation */
@keyframes galleryScroll {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}

.galleryTrack {
  animation: galleryScroll 40s linear infinite;
}

/* Modal animation */
@keyframes modalIn {
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.97);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modalAnimate {
  animation: modalIn 0.3s ease;
}

/* Floating pill pulse */
@keyframes floatPulse {
  0%,
  100% {
    box-shadow: 0 8px 28px rgba(255, 131, 184, 0.4);
  }
  50% {
    box-shadow: 0 8px 36px rgba(255, 131, 184, 0.6);
  }
}

.floatPulse {
  animation: floatPulse 2s ease-in-out infinite;
}

/* Loading bar animation */
.loadingBar {
  transition: width 0.15s linear;
}

/* Mobile dropdown animation */
@keyframes dropIn {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.mobileDropdown {
  animation: dropIn 0.2s ease;
}

/* Scroll progress bar */
.scrollProgress {
  position: fixed;
  top: 0;
  left: 0;
  height: 3px;
  z-index: 9999;
  background: linear-gradient(90deg, #ff83b8, #ff5a9e);
  transition: width 0.1s linear;
}

/* Nav glassmorphism */
.navGlass {
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(16px);
}

.navScrolled {
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

/* Hero character float animation */
@keyframes heroCharIn {
  from {
    opacity: 0;
    transform: translateY(30px) rotate(3deg);
  }
  to {
    opacity: 1;
    transform: translateY(0) rotate(3deg);
  }
}

@keyframes heroFloat {
  0%,
  100% {
    transform: translateY(0) rotate(3deg);
  }
  50% {
    transform: translateY(-10px) rotate(3deg);
  }
}

.heroCharacter {
  /*
   * Hero character is the LCP candidate for the landing page and the
   * only image in the user's first-fold viewport. Running the
   * `heroCharIn` 0.8s opacity-0→1 entrance animation produced a
   * visible "image flickers in" beat on every cold load — the SSR
   * HTML is already in place, so the only thing the entrance animation
   * does is hide a real, fully-loaded image for 0.8s and then fade it
   * back. We keep the steady-state `heroFloat` loop (4s subtle
   * up/down bob) since that is part of the design and does not
   * compete with the SSR paint, but we drop the entrance keyframe so
   * the first frame matches the final paint.
   */
  transform: rotate(3deg);
  animation: heroFloat 4s ease-in-out infinite;
}

@media (prefers-reduced-motion: reduce) {
  .heroCharacter {
    animation: none;
  }
}

/* Hero glow */
.heroGlow {
  position: absolute;
  inset: -40px;
  background: radial-gradient(
    ellipse at center,
    rgba(255, 131, 184, 0.15) 0%,
    transparent 70%
  );
  z-index: -1;
  border-radius: 50%;
}

/* CTA glow box */
.ctaGlow {
  box-shadow: 0 0 60px rgba(255, 131, 184, 0.1);
}

/* Popular badge glow */
@keyframes popularGlow {
  0%,
  100% {
    box-shadow: 0 0 8px rgba(255, 131, 184, 0.4);
  }
  50% {
    box-shadow: 0 0 20px rgba(255, 131, 184, 0.7);
  }
}

.popularBadge {
  animation: popularGlow 2s ease-in-out infinite;
}
